import * as React from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import { TableHead } from '@mui/material'
import ConfirmDelete from '../../../../../components/dialogs/ConfirmDelete'
import EditIcon from '../../../../../assets/icons/Edit/Edit.svg'
import TablePaginationActions from '../../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { LessonScheduleState } from '../../../../../features/types'
import ArrowRightIcon from '../../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'

interface Props {
    lessonsData: LessonScheduleState[]
    onDelete: (id: number) => void
    onEdit: (lesson: LessonScheduleState) => void
}


const LessonsTable: React.FC<Props> = ({ lessonsData = [], onDelete, onEdit }) => {
    const { formatMessage } = useIntl()
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(5)

    const weekDays = [
        { id: 0, name: formatMessage({ id: 'monday' }) },
        { id: 1, name: formatMessage({ id: 'tuesday' }) },
        { id: 2, name: formatMessage({ id: 'wednesday' }) },
        { id: 3, name: formatMessage({ id: 'thursday' }) },
        { id: 4, name: formatMessage({ id: 'friday' }) },
        { id: 5, name: formatMessage({ id: 'saturday' }) },
        { id: 6, name: formatMessage({ id: 'sunday' }) },
    ]

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0
            ? Math.max(0, (1 + page) * rowsPerPage - lessonsData.length)
            : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
            elevation={0}
        >
            <Table
                sx={{ minWidth: 500 }}
                aria-label="custom pagination table"
            >
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                        <TableCell>
                            {formatMessage({ id: 'group' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'subjectName' })} (kz / ru)
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'dayOfWeek' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'startDateTime' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'endDateTime' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'edit' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'link' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'delete' })}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(rowsPerPage > 0
                        ? lessonsData.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                        )
                        : lessonsData
                    ).map((row) => (
                        <TableRow key={row?.id}>
                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.groupName}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row.titleKz} / {row.titleRu}
                            </TableCell>

                            <TableCell
                                style={{ width: 150 }}
                                align="right"
                            >
                                {weekDays[row?.dayOfWeek]?.name}
                            </TableCell>

                            <TableCell
                                style={{ width: 200 }}
                                align="right"
                            >
                                {row?.startTime}
                            </TableCell>

                            <TableCell
                                style={{ width: 200 }}
                                align="right"
                            >
                                {row?.endTime}
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <IconButton
                                    onClick={() => onEdit(row)}
                                    aria-label="edit"
                                >
                                    <img src={EditIcon} alt="edit" />
                                </IconButton>
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <Link
                                    to={`${row?.id}`}
                                    state={{
                                        schedule: row,
                                    }}
                                >
                                    <IconButton sx={{ background: '#f3f4f5' }}>
                                        <img
                                            src={ArrowRightIcon}
                                            alt="details"
                                        />
                                    </IconButton>
                                </Link>
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <ConfirmDelete
                                    confirmAction={() => onDelete(row?.id)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}

                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={7} />
                        </TableRow>
                    )}
                </TableBody>

                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[
                                5,
                                10,
                                25,
                                { label: 'All', value: -1 },
                            ]}
                            colSpan={7}
                            count={lessonsData?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            SelectProps={{
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            sx={{ border: 0 }}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    )
}

export default LessonsTable
